import { useContext, useState, useEffect, useRef, SyntheticEvent } from "react";
import Dialog from "@mui/material/Dialog";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import AddComponent from "./add-component";
import { IAddEditProviderModel } from "../application-upsert-form";
import { ApplicationsContext } from "../applications-context";
import { StyledButton } from "../../../components/styled-button/styled-button";
import { AppContainer, StyledDialogContent, StyledDialogTitle, StyledTypography } from "../../../components/shared-styles/dialog-title-styles";

const AddApplicationComponent = () => {
  const [openAddApplication, setOpenAddApplication] = useState<boolean>(false);
  const { isAppListLoading, selectedApp, setSelectedApp} = useContext(ApplicationsContext);
  const { t } = useTranslation();

  const handleAddClickOpen = () => {
    setOpenAddApplication(true);

    if (selectedApp)
      setSelectedApp("");
  };

  const handleAddApplicationDone = () => {
    setOpenAddApplication(false);
  };

  return (
    <div>
      <AppContainer>
        <StyledButton
          variant="contained"
          onClick={handleAddClickOpen}
          className="d-flex align-items-center"
          disabled={isAppListLoading}
          startIcon={<AddCircleOutlineOutlinedIcon />}
        >
          {t("add_application")}
        </StyledButton>
      </AppContainer>
      <AddApplicationStep
        open={openAddApplication}
        handleAddApplicationClose={() => setOpenAddApplication(false)}
        handleAddApplicationDone={handleAddApplicationDone}
      />
    </div>
  );
};

type IAddApplicationProps = {
  open: boolean;
  handleAddApplicationClose: () => void;
  handleAddApplicationDone: () => void;
};

const AddApplicationStep = (props: IAddApplicationProps) => {
  const { t } = useTranslation();
  const { setUrlLoaded, setAppsLoaded, setSelectedApp, setSelectedCheckedApps } = useContext(ApplicationsContext);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const isProcessingRef = useRef<boolean>(false);

  const defaultModel: IAddEditProviderModel = {
    0: {
      id: "",
      name: "",
      clientId: "",
      accessKey: ""
    },
    isValid: false
  };

  const [model, setModel] = useState<IAddEditProviderModel>(defaultModel);

  const handleClose = (force = false) => {
    if (isProcessingRef.current && !force) {
      return;
    }

    isProcessingRef.current = false;
    setIsProcessing(false);
    setUrlLoaded(false);
    setAppsLoaded(false);
    setSelectedApp(null);
    setSelectedCheckedApps([]);
    props.handleAddApplicationClose();
    setModel(defaultModel);
  };

  const stopInteractionsWhenProcessing = (event: SyntheticEvent) => {
    if (!isProcessingRef.current) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    // Block any additional listeners to close the timing gap between click and state commit.
    (event as SyntheticEvent & { nativeEvent?: { stopImmediatePropagation?: () => void } }).nativeEvent?.stopImmediatePropagation?.();
  };

  const handleProcessingChange = (processing: boolean) => {
    isProcessingRef.current = processing;
    setIsProcessing(processing);
  };

  const handleModelChange = (updatedModel: any) => {
    const updated = updatedModel;
    setModel((prevModel) => ({
      ...prevModel,
      ...updated,
    }));
  };

  useEffect(() => {
    return () => {
      setUrlLoaded(false);
    };
  }, [setUrlLoaded]);

  return (
    <Dialog
      open={props.open}
      PaperProps={{
        style: { maxWidth: '701px' }, // Set custom max width
          }}
      fullWidth={true}
      onClose={(_event, reason) => {
        if (isProcessingRef.current) {
          return;
        }

        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      onKeyDownCapture={stopInteractionsWhenProcessing}
      onKeyUpCapture={stopInteractionsWhenProcessing}
      onMouseDownCapture={stopInteractionsWhenProcessing}
      onPointerDownCapture={stopInteractionsWhenProcessing}
      aria-labelledby="form-dialog-title"
      disableEscapeKeyDown={isProcessing}
    >
      <StyledDialogTitle id="form-dialog-title">
        <StyledTypography>{t("add_new_application")}</StyledTypography>
      </StyledDialogTitle>
      <StyledDialogContent
        onKeyDownCapture={stopInteractionsWhenProcessing}
        onKeyUpCapture={stopInteractionsWhenProcessing}
        onMouseDownCapture={stopInteractionsWhenProcessing}
        onPointerDownCapture={stopInteractionsWhenProcessing}
      >
        <AddComponent
            handleDone={() => {
              handleClose(true);
              props.handleAddApplicationDone();
            }}
            handleClose={handleClose}
            model={model}
            handleModelChange={handleModelChange}
            handleProcessingChange={handleProcessingChange}
        />
      </StyledDialogContent>
    </Dialog>
  );
};

export default AddApplicationComponent;
